import React, { useState } from 'react';
import { Modal } from '../../../components/Modal';
import { Button } from '../../../components/Button';
import { Plus, CheckCircle2, Circle } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { formatCurrency } from '../../../utils/currencyFormatting';
import type { Tab, TabItem, UserProfile, ManualMember } from '../../../types';

interface ItemAssignmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    item: TabItem | null;
    tab: Tab;
    currentUserId: string | undefined;
    memberProfiles: UserProfile[];
    manualMembers: ManualMember[];
    onAssign: (itemId: string, newClaimedBy: string[]) => void;
}

export const ItemAssignmentModal: React.FC<ItemAssignmentModalProps> = ({
    isOpen,
    onClose,
    item,
    tab,
    currentUserId,
    memberProfiles,
    manualMembers,
    onAssign
}) => {
    const [stagedClaims, setStagedClaims] = useState<string[]>(item?.claimedBy ?? []);

    if (!item) return null;

    const quantity = item.quantity ?? 1;
    const isFullyClaimed = quantity > 1 && stagedClaims.length >= quantity;

    const handleToggle = (uid: string) => {
        setStagedClaims(prev => {
            if (prev.includes(uid)) {
                return prev.filter(id => id !== uid);
            }
            return [...prev, uid];
        });
    };

    const handleIncrement = (uid: string) => {
        if (stagedClaims.length < quantity) {
            setStagedClaims(prev => [...prev, uid]);
        }
    };

    const handleDecrement = (uid: string) => {
        setStagedClaims(prev => {
            const newClaims = [...prev];
            const index = newClaims.indexOf(uid);
            if (index > -1) {
                newClaims.splice(index, 1);
            }
            return newClaims;
        });
    };

    const handleSave = () => {
        onAssign(item.id, stagedClaims);
        onClose();
    };

    // Combine members for the list
    const allMembers = [
        ...memberProfiles.map(p => ({
            id: p.uid,
            name: p.displayName,
            isManual: false,
            initials: p.displayName.slice(0, 2).toUpperCase(),
            color: 'bg-primary-100 text-primary-700'
        })),
        ...manualMembers.map(m => ({
            id: m.id,
            name: m.name,
            isManual: true,
            initials: m.name.slice(0, 2).toUpperCase(),
            color: 'bg-amber-100 text-amber-700'
        }))
    ];

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`Assign: ${item.name}`}
        >
            <div className="space-y-4">
                <div className="rounded-xl bg-bg-base p-4">
                    <div className="flex items-baseline justify-between">
                        <p className="text-xs text-text-muted uppercase tracking-wide font-bold">Total Price</p>
                        <p className="text-2xl font-black text-text-heading">
                            {formatCurrency(item.price, tab.currency)}
                        </p>
                    </div>
                    {stagedClaims.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-border-default">
                            <div className="flex items-baseline justify-between">
                                <p className="text-xs text-text-muted">
                                    {quantity > 1 ? "Price Per Item" : "Price Per Person"}
                                </p>
                                <p className="text-lg font-bold text-primary-600">
                                    {formatCurrency(item.price / (quantity > 1 ? quantity : stagedClaims.length), tab.currency)}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                <div>
                    <div className="flex justify-between items-center mb-3">
                        <p className="text-xs text-text-muted uppercase tracking-wide font-bold">Assign To</p>
                        {quantity > 1 && (
                            <p className="text-xs font-bold text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full">
                                {stagedClaims.length} / {quantity} assigned
                            </p>
                        )}
                    </div>
                    
                    <div className="space-y-2 max-h-[40vh] overflow-y-auto overscroll-contain">
                        {allMembers.map((member) => {
                            const claimsCount = stagedClaims.filter(id => id === member.id).length;
                            const isClaimed = claimsCount > 0;

                            return (
                                <div 
                                    key={member.id} 
                                    className={cn(
                                        "flex items-center justify-between rounded-xl border p-3 transition-colors cursor-pointer",
                                        isClaimed ? "border-primary-200 bg-primary-50/50" : "border-border-default",
                                        member.isManual && isClaimed && "border-amber-200 bg-amber-50/30"
                                    )}
                                    onClick={() => quantity === 1 && handleToggle(member.id)}
                                >
                                    <div className="flex items-center gap-3 flex-1 min-w-0 pr-2">
                                        <div className={cn("flex-none flex h-10 w-10 items-center justify-center rounded-full font-bold", member.color)}>
                                            {member.initials}
                                        </div>
                                        <div className="truncate">
                                            <p className="text-sm font-bold text-text-heading truncate">
                                                {member.name} {member.id === currentUserId && "(You)"}
                                            </p>
                                            {member.isManual && (
                                                <p className="text-[10px] text-amber-600 font-medium">Temporary User</p>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="flex-none flex items-center justify-end" onClick={e => quantity > 1 && e.stopPropagation()}>
                                        {quantity === 1 ? (
                                            <div className="text-primary-600">
                                                {isClaimed ? <CheckCircle2 size={24} /> : <Circle size={24} className="text-border-strong" />}
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-3">
                                                <button
                                                    className="h-8 w-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed"
                                                    onClick={() => { handleDecrement(member.id); }}
                                                    disabled={claimsCount === 0}
                                                >
                                                    <div className="w-3 h-0.5 bg-current rounded-full" />
                                                </button>
                                                <span className="text-sm font-bold w-4 text-center">
                                                    {claimsCount}
                                                </span>
                                                <button
                                                    className="h-8 w-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 disabled:opacity-30 disabled:cursor-not-allowed"
                                                    onClick={() => { handleIncrement(member.id); }}
                                                    disabled={isFullyClaimed}
                                                >
                                                    <Plus size={16} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="pt-4 border-t border-border-default flex gap-3">
                    <Button variant="outline" className="flex-1" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button className="flex-1" onClick={handleSave}>
                        Save Assignment
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
